import {
  formatMonarchEvaluations,
  formatPreampExperience,
  MonarchEvaluationsRuleSet,
  MonarchPreampExperience,
} from '@redventures/cohesion-utils-react';
import Cookies from 'js-cookie';
import { setCookie } from '~/helpers/cookie';
import { NextRouter } from 'next/router';
import { Dispatch } from 'redux';
import { DEFAULT_API_VERSION, DEFAULT_ERROR_MESSAGE } from '~/constants/config/common';
import { PAPER_BILL_FEE } from '~/constants/entityIds';
import { PAGE_ROUTES } from '~/constants/pages';
import {
  AiCartBaseBody,
  DynamicRtbsBody,
  TopRecommendationsBody,
  fetchDynamicRtbs,
  fetchTopRecommendations,
} from '~/helpers/dynamicMerchAiHelper';
import clientSide from '~/helpers/clientSide';
import fetch from '~/helpers/fetch';
import getOTKOfferType from '~/helpers/getOTKOfferType';
import { getTopOffer } from '~/helpers/getTopOffer';
import { setNewRelicCustomAttribute } from '~/helpers/newrelic';
import slugify from '~/helpers/slugify';
import { trackEvent } from '~/helpers/tracking';
import { MonarchComponentReturn } from '~/types/monarch';
import { OfferObject } from '~/types/offerObject';
import { setReturnVisitorCookie, hasValidReturnVisitorCookie } from '~/helpers/returnVisitorCookie';
import {
  ActionTypes,
  ClearSelectProductAction,
  FirstAvailableAction,
  GetProductsResponse,
  OfferBannerObject,
  Option,
  Product,
  SelectedProductsState,
  SelectedResumeProductsState,
  SelectProductAction,
  SetIsCopper2FiberAction,
  SetLandingPageProduct,
  SetLandingPageBundle,
  SetLandingPageProductAvailable,
  SetNDIBannerAction,
  SetOfferObjectsAction,
  SetOfferTypeAction,
  SetPhoneSelection,
  SetProductsAction,
  SetSelectedNetflixAction,
  SetSelectedSweetenerAction,
  SetVasSettings,
  SetVRCModalShown,
  SweetenerKeys,
  LandingPageBundle,
  RecommendationsData,
  SetBauRecommendationsAction,
  SetDynamicRTBAction,
  RecommendedRTB,
} from '~/types/products';
import { ReduxState } from '~/types/redux';
import { SetSalesJourney } from '../../types/products';
import {
  addAddressHistory,
  resetAddressRoute,
  setEligibility,
  setTechAvailable,
  setUnservRedirect,
  updateAddress,
} from './address';
import { setPaperBillFee } from './billing';
import { setC2F, setCallInNumber, setcanNavigateToBuyFlowRedirect, setPageLoading, setTracking } from './common';
import { setContact } from './contact-credit';
import { setNotifications } from './global';
import { setLegal } from './legal';
import { formatPhone } from '~/helpers/phone';
import { BUY_FLOW } from '~/hooks/useBuyFlowDecision';
import { buildReturnVisitorHeaders } from '~/helpers/offers';
import { getContext } from '~/helpers/tracking/trackers/tagular';
import { EnrichedCustomer } from '~/types/enrich-customer';
import { WebContext } from '~/types/web-context';
import { getOfferTypes } from '~/helpers/getOfferTypes';

const { EERO, VRC } = SweetenerKeys;

interface PostBody {
  hasExistingService: boolean;
  isCopper2Fiber: boolean;
  isResume?: boolean;
  env?: string;
  controlNumber?: string;
  quoteNumber?: string;
  cartPage?: string;
  isResumeCredit?: boolean;
  mobileVerifyRequired?: string;
  isAi?: boolean;
  predictiveAddressKey: string;
  contractType?: string;
  webContext?: WebContext;
  enrichedCustomer?: EnrichedCustomer;
  altProductExplainerVersion?: 'v2' | 'v3';
}

interface GetProductsBase {
  router: NextRouter;
  cartPage: string;
  isAi?: boolean;
  skipRedirect?: boolean;
  returnVisitorOfferEligibility?: boolean;
}

interface GetProducts extends GetProductsBase {
  isCopper2Fiber: false;
}

interface GetProductsCopper2Fiber extends GetProductsBase {
  isCopper2Fiber: true;
}

export const getProducts = (
  props: GetProducts | GetProductsCopper2Fiber
): ((dispatch: Dispatch, getState: () => ReduxState) => Promise<void>) => {
  const { router, isCopper2Fiber, cartPage, isAi = false, skipRedirect = false, returnVisitorOfferEligibility } = props;
  return async (dispatch: Dispatch, getState: () => ReduxState): Promise<void> => {
    const { address, tracking, eligibility, addressHistory, resume, monarch, offerMerchData, enrichedCustomer } =
      getState();
    const apiVersion = (monarch?.apiVersion as string) || DEFAULT_API_VERSION;
    const addressHasExistingService = eligibility?.addressHasExistingService || false;
    const installAddress = address?.install || {};
    const isResume = resume?.enabled === true;
    const resumeCredit = cartPage === '/contact-credit/' && isResume;
    const resumeProducts = cartPage === '/plan-package/' && isResume;
    const isCopper = address?.techAvailable === 'COPPER';
    // Get topRecommendedEnabled from bauRecommendations state
    const topRecommendedEnabled = isCopper ? false : Boolean(offerMerchData?.productSelection?.topRecommendedEnabled);
    const dynamicRTBsEnabled = isCopper ? false : Boolean(offerMerchData?.productSelection?.dynamicRtBsEnabled);

    const atomContext = getContext();

    const webContext: WebContext = {
      anonymousId: atomContext.anonymousId,
      sessionId: atomContext.sessionId,
      userId: atomContext.anonymousId,
    };

    const postBody: PostBody = {
      // force c2f to always be true since they have a copper line already.
      hasExistingService: isCopper2Fiber ? true : addressHasExistingService,
      isCopper2Fiber,
      isResume,
      isResumeCredit: resumeCredit,
      isAi,
      predictiveAddressKey: address?.install?.addressKey || '',
      contractType: address?.contractType || '',
      webContext,
      enrichedCustomer,
      altProductExplainerVersion: 'v2',
    };

    // check if mobile verification is enabled in experience to pass to OL to enable MCV endpoints
    if (clientSide) {
      window?.cohesion('monarch:done', function (err, evaluations) {
        const rules: MonarchEvaluationsRuleSet = formatMonarchEvaluations(evaluations);
        if (rules?.trafficFlow?.value) {
          const experience = formatPreampExperience(
            rules.trafficFlow.value as unknown as MonarchPreampExperience | null
          );
          if (experience) {
            const placement = experience.placements.find(
              (placement) => placement.placementName === 'address-check-page-config'
            ) as { metadata?: { verifyPhoneEnabled? } };
            const mobileVerificationEnabled = placement?.metadata?.verifyPhoneEnabled === true;
            postBody.mobileVerifyRequired = String(mobileVerificationEnabled).toUpperCase();
          }
        }
      });
    }

    if (isCopper2Fiber === false) {
      postBody.env = installAddress?.env || '';
      postBody.controlNumber = installAddress?.controlNumber || '';
    }

    const headers: HeadersInit = buildReturnVisitorHeaders(
      returnVisitorOfferEligibility ?? (monarch?.returnVisitorOfferEligibility as boolean),
      isCopper
    );

    // First call products/get; then fire dependent AI endpoints in parallel using its response
    const productsResponse = await fetch<GetProductsResponse>({
      endpoint: `${apiVersion}/products/get`,
      options: {
        method: 'POST',
        body: JSON.stringify(postBody),
        headers,
      },
      tracking,
      dispatch,
    });

    const {
      success,
      quoteId,
      quoteNumber,
      accountUuid,
      availableProducts,
      legal,
      userErrorMessage,
      customer,
      gmtOffset,
      serviceAddress,
      firstAvailable,
      productList,
      quoteCreated,
      defaultOffers,
      salesJourney,
      techAvailable,
      offerType,
      redirect,
    } = productsResponse;

    const productIds = availableProducts?.map((product) => product.productId);
    const isMover = Cookies.get('moversSwitchers') === 'movers';
    const safeProductList = productList ?? [];
    const aiCartBaseBody: AiCartBaseBody | null = enrichedCustomer
      ? {
          contractType: address?.contractType || '',
          webContext,
          enrichedCustomer,
          productList: safeProductList,
          mover: isMover,
        }
      : null;

    const topRecommendationsBody: TopRecommendationsBody | null = aiCartBaseBody;
    const dynamicRtbsBody: DynamicRtbsBody | null = aiCartBaseBody
      ? {
          ...aiCartBaseBody,
          productIds: productIds ?? [],
        }
      : null;

    const [topRecsData, dynamicRTBsData] = await Promise.all([
      topRecommendedEnabled && topRecommendationsBody
        ? fetchTopRecommendations({
            apiVersion,
            body: topRecommendationsBody,
            tracking,
            dispatch,
            headers,
          })
        : Promise.resolve(null),
      dynamicRTBsEnabled && dynamicRtbsBody
        ? fetchDynamicRtbs({
            apiVersion,
            body: dynamicRtbsBody,
            tracking,
            dispatch,
            headers,
          })
        : Promise.resolve(null),
    ]);

    try {
      if (redirect?.url) {
        dispatch(setUnservRedirect(redirect));

        if (redirect?.permaleaseNumber) {
          dispatch(setCallInNumber(formatPhone(redirect.permaleaseNumber)));
        }
        if (redirect?.isExternal) {
          window.location.href = redirect.url;
        } else {
          router.push(redirect.url);
        }
      } else if (success) {
        if (firstAvailable) dispatch(setFirstAvailable(firstAvailable));
        if (techAvailable) dispatch(setTechAvailable(techAvailable));
        if (offerType) dispatch(setOfferType(offerType));

        // Set the bau recommendations and dynamic RTBs from dedicated AI endpoints
        dispatch(setBauRecommendations(topRecsData));
        dispatch(setDynamicRTBs(dynamicRTBsData));

        const filteredProducts = (availableProducts || []).filter((product) => product?.price?.amount >= 0);
        const hasProducts = Boolean(filteredProducts.length);

        if (quoteId) {
          dispatch(setTracking('quoteId', quoteId));
          setCookie('quoteId', quoteId, { otGroup: 'C0003' });
          setCookie('quoteCreated', quoteCreated, { otGroup: 'C0003' });
        }
        if (quoteNumber) dispatch(setTracking('quoteNumber', quoteNumber));
        if (accountUuid) dispatch(setTracking('accountUuid', accountUuid));
        if (productList) dispatch(setOfferObjects(productList));
        // Track the users eligibility
        dispatch(
          setEligibility({
            addressHasExistingService,
            serviceable: hasProducts,
          })
        );

        if (serviceAddress) {
          const { addressKey } = serviceAddress;
          dispatch(updateAddress('install', serviceAddress));

          // If a single session repeatedly checks for different addresses,
          // send info to New Relic to flag as a potential bot
          if (addressKey && !addressHistory?.includes(addressKey)) {
            dispatch(addAddressHistory(addressKey));
            if (addressHistory.length >= 2) {
              setNewRelicCustomAttribute([{ name: 'MultipleAddressesCollected', value: 'true' }]);
            }
          }
        }

        //Determines if a user is eligible for specific offers such OFS or hyperlocal offers

        const is1GigPlan = availableProducts?.some(
          (product) => product?.productId === '2b09989e-1696-4cc2-b13c-7e0950b74588'
        );

        if (availableProducts) {
          const monarchOfferRankings = (monarch?.cartOfferRankings as MonarchComponentReturn)?.data
            ?.offerRankingList as OfferBannerObject[];

          const offers = monarchOfferRankings && monarchOfferRankings.length > 0 ? monarchOfferRankings : defaultOffers;
          setCookie('addressOfferType', getOTKOfferType(availableProducts), { otGroup: 'C0003' });

          const { isRVO } = getOfferTypes();
          if (hasValidReturnVisitorCookie() && isRVO) {
            setReturnVisitorCookie({
              shownReturnVisitorOffer: true,
              controlNumber: installAddress?.controlNumber || '',
              env: installAddress?.env || '',
            });
          }

          setCookie('1gig', is1GigPlan, { otGroup: 'C0004' });
          dispatch(setProducts(availableProducts, dispatch, offers));
        }

        dispatch(
          setContact({
            firstname: customer?.firstName || '',
            lastname: customer?.lastName || '',
            email: customer?.email || '',
            phone: customer?.phone || '',
            gmtOffset,
          })
        );

        dispatch(setLegal(legal || {}));

        // installation Before schedule logic
        if (salesJourney) {
          dispatch(setSalesJourney(salesJourney));
        }

        // Skip all redirects if skipRedirect is true
        // Don't set pageLoading to false here - let the redirect hook handle it
        if (skipRedirect) {
          return;
        }

        sessionStorage.setItem('products', JSON.stringify({ availableProducts, offerObjects: productList }));

        if (addressHasExistingService) {
          trackEvent({ action: 'movingToServiced' });
        } else if (hasProducts && !addressHasExistingService) {
          if (!resumeCredit) {
            if (isCopper2Fiber) dispatch(setC2F('enabled', true));
            if (router.pathname === PAGE_ROUTES.RESUME || router.pathname === PAGE_ROUTES.BUY_FIBER) {
              let nextPage = isCopper2Fiber ? PAGE_ROUTES.PLAN_FIBER : PAGE_ROUTES.PLAN_PACKAGE;

              if (Cookies.get('buyFlow') === BUY_FLOW.NGC) {
                if (resumeProducts) {
                  nextPage = PAGE_ROUTES.BUILD;
                } else {
                  dispatch(setPageLoading(false));
                  return;
                }
              }

              router.push(nextPage);
            } else {
              dispatch(setcanNavigateToBuyFlowRedirect(true));
            }
          } else return;
        } else if (isCopper2Fiber && !hasProducts) {
          dispatch(setcanNavigateToBuyFlowRedirect(true));
        } else {
          setNewRelicCustomAttribute([{ name: 'getProductsRouteMatch', value: 'false' }]);
          throw new Error(`getProducts next route not matched`);
        }
      } else {
        setNewRelicCustomAttribute([{ name: 'getProductsError', value: 'true' }]);
        throw new Error(`getProducts not successful`);
      }
    } catch (error) {
      console.log(error);
      dispatch(
        setNotifications([
          {
            type: 'error',
            message: userErrorMessage || DEFAULT_ERROR_MESSAGE,
          },
        ])
      );

      // Skip redirection on error if skipRedirect is true
      // Don't set pageLoading to false here - let the redirect hook handle it
      if (skipRedirect) {
        return;
      }

      router.push(PAGE_ROUTES.ADDRESS_CHECK, undefined, { shallow: true });
      dispatch(setPageLoading(false));

      dispatch(resetAddressRoute());
    }
  };
};

export const setProducts = (
  products: Product[],
  dispatch: Dispatch,
  offerRankingList?: OfferBannerObject[]
): SetProductsAction => {
  const available = {};
  const totalProducts = products?.length;
  const addressOfferType = Cookies.get('addressOfferType') ?? 'default';

  const offerLevelSweeteners = {
    has2Gig: false,
    sweetenerLegalKeys: [],
  };

  for (let i = 0; i < totalProducts; i++) {
    const product = products[i];
    const type = slugify(product.type);
    product.is200 = product?.dataspeed?.downloadInMbps === 200;
    product.is500 = product?.dataspeed?.downloadInMbps === 500;
    product.is1Gig = product?.dataspeed?.downloadInMbps === 1000;
    product.is2Gig = product?.dataspeed?.downloadInMbps === 2000;
    product.is5Gig = product?.dataspeed?.downloadInMbps === 5000;
    product.is7Gig = product?.dataspeed?.downloadInMbps === 7000;

    if (product?.dataspeed?.downloadInMbps === 2000) {
      offerLevelSweeteners.has2Gig = true;
    }

    if (product?.sweeteners?.eero && !offerLevelSweeteners.sweetenerLegalKeys.includes(EERO))
      offerLevelSweeteners.sweetenerLegalKeys.push(EERO);
    // Allows for dynamic paper bill fee
    if (product?.productId === PAPER_BILL_FEE) dispatch(setPaperBillFee(product?.price));

    // Get the top ranked offer
    if (offerRankingList) {
      const topOffer = getTopOffer(offerRankingList, product, addressOfferType);

      if (topOffer) {
        product.topOffer = topOffer;
      }

      // Find the top VRC offer for this product
      const vrcOffers = offerRankingList
        .filter((offer) => offer.productName === product.name && offer.offerType === 'VRC')
        .sort((a, b) => a.offerRank - b.offerRank);

      if (vrcOffers.length > 0) {
        product.vrcOffer = vrcOffers[0];
      }
    }

    if (!type) continue;

    if (available[type]) {
      available[type] = [...available[type], product].sort((a, b) => a.price.amount - b.price.amount);
    } else {
      available[type] = [product];
    }
  }
  return { type: ActionTypes.SET_PRODUCTS, available, offerLevelSweeteners };
};

export const setOfferType = (offerType: string): SetOfferTypeAction => {
  return { type: ActionTypes.SET_OFFER_TYPE, offerType };
};

export const setOfferObjects = (offerObjects: OfferObject[]): SetOfferObjectsAction => {
  return { type: ActionTypes.SET_OFFER_OBJECTS, offerObjects };
};

export const selectProduct = (
  selectedState: SelectedResumeProductsState | SelectedProductsState,
  isCopper: boolean
): SelectProductAction => {
  const { internet } = selectedState || {};
  const is200 = internet?.is200;
  const is500 = internet?.is500;
  const is1Gig = internet?.is1Gig;
  const is2Gig = internet?.is2Gig;
  const is5Gig = internet?.is5Gig;
  const is7Gig = internet?.is7Gig;

  // Data needed on the order details sidebar sweetener component
  const detailsBanner = [];
  // eero
  if (Boolean(internet?.sweeteners?.[EERO] || internet?.is2Gig || internet?.is5Gig || internet?.is7Gig)) {
    detailsBanner.push({
      name: 'eero',
      text: `${
        isCopper
          ? 'Amazon eero Wi-Fi router included'
          : is200
          ? 'Amazon eero Pro 6E router included'
          : is500
          ? 'Amazon eero Pro 7 router included'
          : is1Gig
          ? 'Amazon eero Pro 7 router included'
          : is2Gig
          ? 'Amazon eero Pro 7 router included'
          : is5Gig
          ? 'Amazon eero Max 7 router included'
          : is7Gig
          ? '2 Amazon eero Max 7 devices included'
          : ''
      }`,
    });
  }
  // vrc
  if (Boolean(internet?.sweeteners?.[VRC])) {
    detailsBanner.push({
      name: 'vrc',
      text: `+$${internet?.sweeteners?.[VRC]?.amount} Visa® Reward Card`,
    });
  }

  internet.detailsBanner = detailsBanner;

  return { type: ActionTypes.SELECT_PRODUCT, selectedState };
};

export const clearSelectProduct = (): ClearSelectProductAction => {
  return { type: ActionTypes.CLEAR_SELECT_PRODUCT };
};

export const setSelectedSweetener = (selectedSweetener: Option): SetSelectedSweetenerAction => {
  return { type: ActionTypes.SET_SELECTED_SWEETENER, selectedSweetener };
};

export const setSelectedNetflix = (selectedNetflix: boolean): SetSelectedNetflixAction => {
  return { type: ActionTypes.SET_NETFLIX_BUNDLE, selectedNetflix };
};

export const setVrcModalShown = (vrcModalShown: boolean): SetVRCModalShown => {
  return { type: ActionTypes.SET_VRC_MODAL_SHOWN, vrcModalShown };
};

export const setPhoneSelection = (phoneSelection: string): SetPhoneSelection => {
  return { type: ActionTypes.SET_PHONE_SELECTION, phoneSelection };
};

export const setFirstAvailable = (firstAvailable: string): FirstAvailableAction => {
  return { type: ActionTypes.SET_FIRST_AVAILABLE, firstAvailable };
};

export const setNDIBanner = (ndiBanner: boolean): SetNDIBannerAction => {
  return { type: ActionTypes.SET_NDI_BANNER, ndiBanner };
};

export const setLandingPageProduct = (landingPageProduct: string): SetLandingPageProduct => {
  return { type: ActionTypes.SET_LANDING_PAGE_PRODUCT, landingPageProduct };
};

export const setLandingPageBundle = (landingPageBundle: LandingPageBundle): SetLandingPageBundle => {
  return { type: ActionTypes.SET_LANDING_PAGE_BUNDLE, landingPageBundle };
};

export const setLandingPageProductAvailable = (
  landingPageProductAvailable: boolean
): SetLandingPageProductAvailable => {
  return {
    type: ActionTypes.SET_LANDING_PAGE_PRODUCT_AVAILABLE,
    landingPageProductAvailable,
  };
};

export const setVasSettings = (
  hasVas: boolean,
  hasVasVersion: string,
  hasVasSubmitTextButton: string,
  vasAlias: string
): SetVasSettings => {
  return { type: ActionTypes.SET_VAS_SETTINGS, hasVas, hasVasVersion, hasVasSubmitTextButton, vasAlias };
};

export const setSalesJourney = (salesJourney: string): SetSalesJourney => {
  return { type: ActionTypes.SET_SALES_JOURNEY, salesJourney };
};

export const setIsCopper2Fiber = (isCopper2Fiber: boolean): SetIsCopper2FiberAction => {
  return { type: ActionTypes.SET_IS_COPPER_2_FIBER, isCopper2Fiber };
};

export const setBauRecommendations = (recommendations?: RecommendationsData | null): SetBauRecommendationsAction => {
  if (!recommendations) {
    return {
      type: ActionTypes.SET_BAU_RECOMMENDATIONS,
      recommendations: {
        recommendations: [],
        rankedVAS: [],
        rankedSpeeds: [],
        isDefault: false,
      },
    };
  }

  return { type: ActionTypes.SET_BAU_RECOMMENDATIONS, recommendations };
};

const setDynamicRTBs = (recommendedRTBs?: Record<string, RecommendedRTB> | null): SetDynamicRTBAction => {
  if (!recommendedRTBs) {
    return { type: ActionTypes.SET_DYNAMIC_RTB, recommendedRTBs: {} };
  }
  return { type: ActionTypes.SET_DYNAMIC_RTB, recommendedRTBs };
};
